import { useEffect, useState } from "react"
import CurrencyInputField, {
  type CurrencyInputProps as LibraryCurrencyInputProps,
} from "react-currency-input-field"

import { cn } from "../../lib/utils"

const toText = (value: number): string => (value === 0 ? "" : String(value))
const toNumber = (text: string): number => (text === "" ? 0 : Number(text))

export interface CurrencyInputProps
  extends Omit<LibraryCurrencyInputProps, "value" | "onValueChange"> {
  value: number
  onValueChange: (value: number) => void
  /** A short static label shown inside the field, before the typed amount — e.g. a fixed currency code. */
  adornment?: string
}

/**
 * A number input that renders with thousands separators while typing, e.g.
 * `250,000.00`, following US accounting format.
 */
function CurrencyInput({
  className,
  value,
  onValueChange,
  adornment,
  ...props
}: CurrencyInputProps) {
  // The field holds the text being typed, not the parsed number. Feeding the
  // number back made every keystroke re-render as a finished amount ("1"
  // became "1.00"), so the next digits landed after the decimal point and
  // typing 1000 produced 10. Two decimals are applied on blur instead, with
  // decimalScale: fixedDecimalLength looks like the same thing and is not, it
  // reads a whole number as cents, so 1000 became 10.00 on blur.
  const [text, setText] = useState(() => toText(value))

  // The number still wins when it changes from outside: a form reset, a loaded
  // record, or "use suggested amount". Typing never trips this, because the
  // text typed and the number reported are then the same amount.
  useEffect(() => {
    setText((current) => (toNumber(current) === value ? current : toText(value)))
  }, [value])

  const field = (
    <CurrencyInputField
      data-slot="input"
      value={text}
      decimalsLimit={2}
      decimalScale={2}
      allowNegativeValue={false}
      intlConfig={{ locale: "en-US" }}
      onValueChange={(next, _name, values) => {
        setText(next ?? "")
        onValueChange(values?.float ?? 0)
      }}
      className={cn(
        "h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 font-mono text-base tabular-nums shadow-xs transition-[color,box-shadow,border-color,background-color] duration-150 ease-out outline-none selection:bg-primary selection:text-primary-foreground placeholder:text-placeholder hover:border-ring/40 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
        adornment && "pl-14",
        className
      )}
      {...props}
    />
  )

  if (!adornment) return field

  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
        {adornment}
      </span>
      {field}
    </div>
  )
}

export { CurrencyInput }
